import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useTutorial } from './context/TutorialContext'
import { ReactComponent as CloseIcon } from 'assets/images/close.svg'
import { ReactComponent as MinimizeIcon } from 'assets/images/minus.svg'

const MiniTutorialPlayer = () => {
  const { floatingTutorial, closeFloatingPlayer } = useTutorial()
  const [minimized, setMinimized] = useState(false)
  const [interactionActive, setInteractionActive] = useState(false)
  const [activeEdge, setActiveEdge] = useState(null)
  const [isDraggingOrResizing, setIsDraggingOrResizing] = useState(false)
  const [isPlaying, setIsPlaying] = useState(true)

  const playerRef = useRef(null)
  const iframeRef = useRef(null)
  const posRef = useRef({ x: 20, y: window.innerHeight - 300 > 0 ? window.innerHeight - 300 : 20 })
  const sizeRef = useRef({ width: 320, height: 180 })
  const dragRef = useRef({ active: false, offsetX: 0, offsetY: 0 })
  const resizeRef = useRef({ active: false, dir: null })

  const MIN_W = 280
  const MIN_H = 160
  const MARGIN = 10
  let frame = null

  const getClientCoords = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY }
    }
    return { x: e.clientX, y: e.clientY }
  }

  const extractSrc = (html) => {
    const match = html.match(/src="([^"]+)"/)
    return match ? match[1] : null
  }

  const startDrag = (e) => {
    if (resizeRef.current.active) return
    if (e.target.closest('button')) return;

    const { x, y } = getClientCoords(e)

    dragRef.current.active = true
    dragRef.current.offsetX = x - posRef.current.x
    dragRef.current.offsetY = y - posRef.current.y
    setInteractionActive(true)
    setIsDraggingOrResizing(true)
    document.body.style.userSelect = 'none'
  }

  const startResize = (e, dir) => {
    e.stopPropagation()
    const { x, y } = getClientCoords(e)
    const rect = playerRef.current.getBoundingClientRect()
    resizeRef.current = {
      active: true,
      dir,
      startX: x,
      startY: y,
      startW: rect.width,
      startH: rect.height,
      startXPos: posRef.current.x,
      startYPos: posRef.current.y,
    }
    setActiveEdge(dir)
    setInteractionActive(true)
    setIsDraggingOrResizing(true)
    document.body.style.userSelect = 'none'
  }

  const handleMove = (e) => {
    if (!playerRef.current) return

    if (!dragRef.current.active && !resizeRef.current.active) return

    if (e.type === 'touchmove') {
      // e.preventDefault()
    }

    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      const { innerWidth, innerHeight } = window
      const { x, y } = getClientCoords(e)

      // === DRAG ===
      if (dragRef.current.active) {
        const rect = playerRef.current.getBoundingClientRect()
        const width = rect.width
        const height = rect.height

        let newX = x - dragRef.current.offsetX
        let newY = y - dragRef.current.offsetY

        newX = Math.max(MARGIN, Math.min(newX, innerWidth - width - MARGIN))
        newY = Math.max(MARGIN, Math.min(newY, innerHeight - height - MARGIN))

        posRef.current = { x: newX, y: newY }
        playerRef.current.style.transform = `translate3d(${newX}px, ${newY}px, 0)`
      }

      if (resizeRef.current.active) {
        const { startX, startY, startW, startH, dir, startXPos, startYPos } = resizeRef.current

        let deltaX = x - startX
        let deltaY = y - startY
        let newWidth = startW
        let newHeight = startH
        let newX = startXPos
        let newY = startYPos

        if (dir.includes('e')) newWidth = startW + deltaX
        if (dir.includes('s')) newHeight = startH + deltaY
        if (dir.includes('w')) { newWidth = startW - deltaX; newX = startXPos + deltaX }
        if (dir.includes('n')) { newHeight = startH - deltaY; newY = startYPos + deltaY }

        if (newWidth < MIN_W) { if (dir.includes('w')) newX += newWidth - MIN_W; newWidth = MIN_W }
        if (newHeight < MIN_H) { if (dir.includes('n')) newY += newHeight - MIN_H; newHeight = MIN_H }

        const maxW = innerWidth - newX - MARGIN
        const maxH = innerHeight - newY - MARGIN
        newWidth = Math.min(newWidth, maxW)
        newHeight = Math.min(newHeight, maxH)

        sizeRef.current = { width: newWidth, height: newHeight }
        posRef.current = { x: newX, y: newY }

        playerRef.current.style.width = `${newWidth}px`
        playerRef.current.style.height = `${newHeight}px`
        playerRef.current.style.transform = `translate3d(${newX}px, ${newY}px, 0)`
      }
    })
  }

  const handleUp = () => {
    dragRef.current.active = false
    resizeRef.current.active = false
    setInteractionActive(false)
    setActiveEdge(null)
    setIsDraggingOrResizing(false)
    document.body.style.userSelect = 'auto'
  }

  useEffect(() => {
    const options = { passive: false };
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    window.addEventListener('touchmove', handleMove, options)
    window.addEventListener('touchend', handleUp)

    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('touchend', handleUp)
    }
  }, [])

  useEffect(() => {
    const w = window.innerWidth
    if (w < 450) {
      sizeRef.current = { width: w - 40, height: (w - 40) * 0.5625 }
      posRef.current = { x: 20, y: window.innerHeight - 250 }
    } else {
      sizeRef.current = { width: 420, height: 236 }
    }
  }, [])

  const handleVideoClick = () => {
    if (!iframeRef.current) return
    const src = iframeRef.current.src
    if (src.includes('youtube.com/embed')) {
      const command = isPlaying ? 'pauseVideo' : 'playVideo'
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: command, args: [] }),
        '*'
      )
      setIsPlaying(!isPlaying)
    }
  }

  if (!floatingTutorial) return null
  const videoSrc = extractSrc(floatingTutorial.iframe)
  const ytSrc = videoSrc?.includes('enablejsapi=1')
    ? videoSrc
    : `${videoSrc}${videoSrc?.includes('?') ? '&' : '?'}enablejsapi=1`

  const isMobile = window.innerWidth <= 768;

  return (
    <>
      {interactionActive && <GlassPane />}
      <Wrapper
        ref={playerRef}
        data-moving={isDraggingOrResizing ? 'true' : 'false'}
        style={{
          width: minimized ? 280 : sizeRef.current.width,
          height: minimized ? 60 : sizeRef.current.height,
          transform: `translate3d(${posRef.current.x}px, ${posRef.current.y}px, 0)`
        }}
      >
        <Header
          onMouseDown={startDrag}
          onTouchStart={startDrag}
        >
          <Title>{floatingTutorial.title}</Title>
          <Actions>
            <button
              onClick={() => setMinimized(!minimized)}
              onTouchEnd={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <MinimizeIcon />
            </button>
            <button
              onClick={closeFloatingPlayer}
              onTouchEnd={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <CloseIcon />
            </button>
          </Actions>
        </Header>

        {!minimized && (
          <VideoWrapper
            isDisabled={isDraggingOrResizing}
            onClick={handleVideoClick}
          >
            <iframe
              ref={iframeRef}
              src={ytSrc}
              title={floatingTutorial.title}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </VideoWrapper>
        )}

        {!minimized && !isMobile && (
          <>
            {['n','s','e','w'].map(pos => (
              <ResizeEdge
                key={pos}
                pos={pos}
                active={activeEdge === pos}
                onMouseDown={(e) => startResize(e, pos)}
              />
            ))}
            {['ne','nw','se','sw'].map(pos => (
              <ResizeCorner
                key={pos}
                pos={pos}
                active={activeEdge === pos}
                onMouseDown={(e) => startResize(e, pos)}
              />
            ))}
          </>
        )}
      </Wrapper>
    </>
  )
}

export default MiniTutorialPlayer

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f6f8fc;
  padding: 8px 12px;
  font-weight: 600;
  color: #333;
  cursor: grab;
  touch-action: none;
`

const GlassPane = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99998;
  cursor: default;
`

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  background: #fff;
  border-radius: 10px;
  z-index: 2147483647;
  isolation: isolate;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  user-select: none;
  will-change: transform;
  box-shadow: 0 4px 18px rgba(0,0,0,0.25);
  opacity: 1;
  transition: box-shadow 0.2s ease, opacity 0.2s ease;
  max-width: 100vw;
  max-height: 100vh;
`

const Title = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #222;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Actions = styled.div`
  display: flex;
  gap: 6px;
  button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`

const VideoWrapper = styled.div`
  flex: 1;
  position: relative;
  z-index: 1;
  background: #000;
  transform: translateZ(0);
  will-change: transform;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};
    transform: translateZ(0);
  }
`

const ResizeEdge = styled.div`
  position: absolute;
  ${({ pos }) => (pos === 'n' || pos === 's') && `
    left: -5px; right: -5px; height: 10px;
    ${pos === 'n' ? 'top: -5px;' : 'bottom: -5px;'}
  `}
  ${({ pos }) => (pos === 'e' || pos === 'w') && `
    top: -5px; bottom: -5px; width: 10px;
    ${pos === 'e' ? 'right: -5px;' : 'left: -5px;'}
  `}
  cursor: ${({ pos }) => pos}-resize;
  z-index: 10;
  ${({ active }) => active && `
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.6) inset;
  `}
`

const ResizeCorner = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  z-index: 11;
  ${({ pos }) => {
  switch (pos) {
    case 'ne': return 'top: -6px; right: -6px; cursor: ne-resize;'
    case 'nw': return 'top: -6px; left: -6px; cursor: nw-resize;'
    case 'se': return 'bottom: -6px; right: -6px; cursor: se-resize;'
    case 'sw': return 'bottom: -6px; left: -6px; cursor: sw-resize;'
    default: return ''
  }
}}
  ${({ active }) => active && `
    background: rgba(26, 115, 232, 0.6);
    border-radius: 2px;
  `}
`
